import { Image } from "@tiptap/extension-image";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
	Bold,
	Code,
	Heading2,
	Heading3,
	ImageIcon,
	Italic,
	List,
	ListOrdered,
	Redo,
	TableIcon,
	Undo,
} from "lucide-react";
import { useCallback, useId, useRef, useState } from "react";
import { Markdown } from "tiptap-markdown";
import { cn } from "@/lib/utils";
import { Separator } from "./separator";
import { Toggle } from "./toggle";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

const MAX_IMAGE_BYTES = 1 * 1024 * 1024; // 1 MB

function fileToDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
		reader.onload = () => {
			const result = reader.result;
			if (typeof result !== "string") {
				reject(new Error("Unexpected file reader result"));
				return;
			}
			resolve(result);
		};
		reader.readAsDataURL(file);
	});
}

interface MarkdownEditorProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	className?: string;
	ariaLabelledBy?: string;
	/** Format for `value`/`onChange`; `"html"` edits raw HTML (e.g. email signatures) — raw-text fallback edits the HTML string directly. */
	format?: "markdown" | "html";
}

function ToolbarButton({
	pressed,
	onPressedChange,
	tooltip,
	children,
	disabled,
}: {
	pressed?: boolean;
	onPressedChange: () => void;
	tooltip: string;
	children: React.ReactNode;
	disabled?: boolean;
}) {
	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<Toggle
						size="sm"
						aria-label={tooltip}
						pressed={pressed}
						onPressedChange={onPressedChange}
						disabled={disabled}
					>
						{children}
					</Toggle>
				}
			/>
			<TooltipContent side="bottom">{tooltip}</TooltipContent>
		</Tooltip>
	);
}

function EditorToolbar({
	editor,
	rawMode,
	onToggleRawMode,
	onInsertImage,
}: {
	editor: ReturnType<typeof useEditor>;
	rawMode: boolean;
	onToggleRawMode: () => void;
	onInsertImage: () => void;
}) {
	return (
		<div className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1">
			{!rawMode && editor && (
				<>
					<ToolbarButton
						tooltip="Bold"
						pressed={editor.isActive("bold")}
						onPressedChange={() => editor.chain().focus().toggleBold().run()}
					>
						<Bold className="size-4" />
					</ToolbarButton>
					<ToolbarButton
						tooltip="Italic"
						pressed={editor.isActive("italic")}
						onPressedChange={() => editor.chain().focus().toggleItalic().run()}
					>
						<Italic className="size-4" />
					</ToolbarButton>

					<Separator orientation="vertical" className="mx-1 h-5" />

					<ToolbarButton
						tooltip="Heading 2"
						pressed={editor.isActive("heading", { level: 2 })}
						onPressedChange={() =>
							editor.chain().focus().toggleHeading({ level: 2 }).run()
						}
					>
						<Heading2 className="size-4" />
					</ToolbarButton>
					<ToolbarButton
						tooltip="Heading 3"
						pressed={editor.isActive("heading", { level: 3 })}
						onPressedChange={() =>
							editor.chain().focus().toggleHeading({ level: 3 }).run()
						}
					>
						<Heading3 className="size-4" />
					</ToolbarButton>

					<Separator orientation="vertical" className="mx-1 h-5" />

					<ToolbarButton
						tooltip="Bullet List"
						pressed={editor.isActive("bulletList")}
						onPressedChange={() =>
							editor.chain().focus().toggleBulletList().run()
						}
					>
						<List className="size-4" />
					</ToolbarButton>
					<ToolbarButton
						tooltip="Numbered List"
						pressed={editor.isActive("orderedList")}
						onPressedChange={() =>
							editor.chain().focus().toggleOrderedList().run()
						}
					>
						<ListOrdered className="size-4" />
					</ToolbarButton>

					<Separator orientation="vertical" className="mx-1 h-5" />

					<ToolbarButton
						tooltip="Insert Table"
						pressed={false}
						onPressedChange={() =>
							editor
								.chain()
								.focus()
								.insertTable({ rows: 3, cols: 3, withHeaderRow: true })
								.run()
						}
					>
						<TableIcon className="size-4" />
					</ToolbarButton>
					<ToolbarButton
						tooltip="Insert Image"
						pressed={false}
						onPressedChange={onInsertImage}
					>
						<ImageIcon className="size-4" />
					</ToolbarButton>

					<Separator orientation="vertical" className="mx-1 h-5" />

					<ToolbarButton
						tooltip="Undo"
						pressed={false}
						onPressedChange={() => editor.chain().focus().undo().run()}
						disabled={!editor.can().undo()}
					>
						<Undo className="size-4" />
					</ToolbarButton>
					<ToolbarButton
						tooltip="Redo"
						pressed={false}
						onPressedChange={() => editor.chain().focus().redo().run()}
						disabled={!editor.can().redo()}
					>
						<Redo className="size-4" />
					</ToolbarButton>
				</>
			)}

			<div className="ml-auto">
				<ToolbarButton
					tooltip={rawMode ? "Switch to rich editor" : "Switch to Markdown"}
					pressed={rawMode}
					onPressedChange={onToggleRawMode}
				>
					<Code className="size-4" />
				</ToolbarButton>
			</div>
		</div>
	);
}

function MarkdownEditor({
	value,
	onChange,
	placeholder,
	className,
	ariaLabelledBy,
	format = "markdown",
}: MarkdownEditorProps) {
	const isHtml = format === "html";
	const [rawMode, setRawMode] = useState(false);
	// Track whether we're programmatically setting content to avoid feedback loops
	const suppressUpdate = useRef(false);
	// Use a ref so the onCreate closure always sees the latest value
	const valueRef = useRef(value);
	valueRef.current = value;
	const lastSyncedValue = useRef<string | null>(null);

	const handleUpdate: NonNullable<Parameters<typeof useEditor>[0]["onUpdate"]> =
		useCallback(
			({ editor: e }) => {
				if (suppressUpdate.current) return;
				if (isHtml) {
					const html = e.getHTML();
					lastSyncedValue.current = html;
					onChange(html);
					return;
				}
				const markdownStorage = Object.getOwnPropertyDescriptor(
					e.storage,
					"markdown",
				)?.value;
				if (
					markdownStorage &&
					typeof markdownStorage === "object" &&
					"getMarkdown" in markdownStorage &&
					typeof markdownStorage.getMarkdown === "function"
				) {
					const md = markdownStorage.getMarkdown() as string;
					lastSyncedValue.current = md;
					onChange(md);
				}
			},
			[onChange, isHtml],
		);

	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const fileInputId = useId();
	const editorRef = useRef<ReturnType<typeof useEditor> | null>(null);
	const [imageError, setImageError] = useState<string | null>(null);

	const insertImageFromFile = useCallback(async (file: File) => {
		if (!file.type.startsWith("image/")) {
			setImageError("Selected file is not an image.");
			return;
		}
		if (file.size > MAX_IMAGE_BYTES) {
			setImageError(
				`Image is too large (${Math.round(
					file.size / 1024,
				)} KB, max ${MAX_IMAGE_BYTES / 1024} KB).`,
			);
			return;
		}
		try {
			const dataUrl = await fileToDataUrl(file);
			editorRef.current
				?.chain()
				.focus()
				.setImage({ src: dataUrl, alt: file.name })
				.run();
			setImageError(null);
		} catch (err) {
			setImageError(err instanceof Error ? err.message : String(err));
		}
	}, []);

	const editor = useEditor({
		extensions: [
			StarterKit,
			Table.configure({ resizable: false }),
			TableRow,
			TableHeader,
			TableCell,
			Image.configure({ inline: true, allowBase64: true }),
			Placeholder.configure({ placeholder: placeholder ?? "Start writing..." }),
			Markdown.configure({
				html: isHtml,
				transformPastedText: true,
				transformCopiedText: true,
			}),
		],
		editorProps: {
			attributes: ariaLabelledBy
				? {
						"aria-label": placeholder ?? "Markdown editor",
						"aria-labelledby": ariaLabelledBy,
						role: "textbox",
						"aria-multiline": "true",
					}
				: {
						"aria-label": placeholder ?? "Markdown editor",
						role: "textbox",
						"aria-multiline": "true",
					},
			handlePaste: (_view, event) => {
				const items = event.clipboardData?.items;
				if (!items) return false;
				for (const item of items) {
					if (item.kind === "file" && item.type.startsWith("image/")) {
						const file = item.getAsFile();
						if (file) {
							event.preventDefault();
							void insertImageFromFile(file);
							return true;
						}
					}
				}
				return false;
			},
			handleDrop: (_view, event) => {
				const files = event.dataTransfer?.files;
				if (!files || files.length === 0) return false;
				const file = files[0];
				if (!file.type.startsWith("image/")) return false;
				event.preventDefault();
				void insertImageFromFile(file);
				return true;
			},
		},
		// content prop is parsed as HTML not markdown, so set via onCreate instead.
		// Suppress onUpdate to avoid locking in initial (possibly empty) content.
		onCreate: ({ editor: e }) => {
			const current = valueRef.current;
			if (current) {
				suppressUpdate.current = true;
				e.commands.setContent(current);
				suppressUpdate.current = false;
				lastSyncedValue.current = current;
			}
		},
		onUpdate: handleUpdate,
	});

	editorRef.current = editor;

	// Sync external value changes into the editor (for non-raw mode)
	if (editor && !rawMode && value !== lastSyncedValue.current) {
		lastSyncedValue.current = value;
		suppressUpdate.current = true;
		editor.commands.setContent(value);
		suppressUpdate.current = false;
	}

	const handleToggleRawMode = () => {
		if (rawMode && editor) {
			// Switching from raw → rich: push current markdown into tiptap
			suppressUpdate.current = true;
			editor.commands.setContent(value);
			suppressUpdate.current = false;
		}
		setRawMode((prev) => !prev);
	};

	const handleInsertImageClick = () => {
		setImageError(null);
		fileInputRef.current?.click();
	};

	const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		// Reset value so re-picking the same file refires onChange.
		e.target.value = "";
		if (file) void insertImageFromFile(file);
	};

	return (
		<div
			aria-labelledby={ariaLabelledBy}
			className={cn(
				"rounded-lg border border-input bg-transparent transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 dark:bg-input/30",
				className,
			)}
		>
			<EditorToolbar
				editor={editor}
				rawMode={rawMode}
				onToggleRawMode={handleToggleRawMode}
				onInsertImage={handleInsertImageClick}
			/>
			<label htmlFor={fileInputId} className="sr-only">
				Upload image
			</label>
			<input
				ref={fileInputRef}
				type="file"
				id={fileInputId}
				accept="image/*"
				hidden
				onChange={handleFileChosen}
			/>
			{rawMode ? (
				<textarea
					aria-labelledby={ariaLabelledBy}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					className="min-h-[350px] w-full resize-y bg-transparent px-3 py-2 font-mono text-sm outline-none placeholder:text-muted-foreground"
				/>
			) : (
				<EditorContent
					editor={editor}
					className="prose prose-sm dark:prose-invert max-w-none px-3 py-2 focus:outline-none [&_.tiptap]:min-h-[350px] [&_.tiptap]:outline-none [&_.tiptap_img]:max-w-full [&_.tiptap_img]:h-auto [&_.tiptap_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none [&_.tiptap_p.is-editor-empty:first-child::before]:h-0 [&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.tiptap_table]:border-collapse [&_.tiptap_td]:border [&_.tiptap_td]:border-border [&_.tiptap_td]:p-2 [&_.tiptap_th]:border [&_.tiptap_th]:border-border [&_.tiptap_th]:bg-muted [&_.tiptap_th]:p-2 [&_.tiptap_th]:font-semibold"
				/>
			)}
			{imageError ? (
				<div className="border-t bg-destructive/10 px-3 py-2 text-xs text-destructive">
					{imageError}
				</div>
			) : null}
		</div>
	);
}

export type { MarkdownEditorProps };
export { MarkdownEditor };
